import { useDebounceEffect } from 'ahooks';
import { IGlobalSearchResultGroup, IGlobalSearchSuggestionGroup } from 'odc-shared/components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ModuleSearchPostsReq, ModuleSupportCenterPost } from '@src/api/generated/data-contracts';
import { postSupportSearch } from '@src/api/generated/Support';
import useMobxActions from '@src/hooks/useMobxActions';
import useMobxSelector from '@src/hooks/useMobxSelector';
import useNav from '@src/hooks/useNav';
import { getCategoryFirstPid } from '@src/utils/helpCenter';

// 搜索相关类型定义
export interface SearchState {
  keyword: string;
  results: ModuleSupportCenterPost[];
  loading: boolean;
  selectedCategory: string;
  total: number;
}

interface SearchActions {
  setKeyword: (keyword: string) => void;
  setSelectedCategory: (category: string) => void;
  performSearch: (keyword: string) => Promise<void>;
}

export interface SupportSearchReturn {
  searchState: SearchState;
  searchActions: SearchActions;
  searchSuggestions: IGlobalSearchSuggestionGroup[];
  searchResults: IGlobalSearchResultGroup[];
  showGlobalSearch: boolean;
  setShowGlobalSearch: (show: boolean) => void;
  handleSearchChange: (e: unknown, value: string) => void;
  handleSearchClose: () => void;
  handleViewMore: () => void;
}

function useSupportSearch(): SupportSearchReturn {
  const actions = useMobxActions();
  const nav = useNav();

  // 搜索相关状态
  const [searchState, setSearchState] = useState<SearchState>({
    keyword: '',
    results: [],
    loading: false,
    selectedCategory: 'all',
    total: 0,
  });

  // 全局搜索相关
  const [showGlobalSearch, setShowGlobalSearch] = useState<boolean>(false);

  const catalogsByPlatform = useMobxSelector((stores) => stores.helpCenterStore.catalogsByPlatform);

  // 组件挂载时请求数据
  useEffect(() => {
    // 请求两个平台的目录结构
    actions.helpCenterStore.fetchCatalogs('publisher');
    actions.helpCenterStore.fetchCatalogs('advertiser');
    // 请求热门文章
    actions.helpCenterStore.fetchPopularPosts();
  }, [actions]);

  // 搜索函数
  const performSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setSearchState(prev => ({ ...prev, results: [], loading: false }));
      return;
    }

    setSearchState(prev => ({ ...prev, loading: true }));
    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: keyword.trim(),
        page: 0,
        pageSize: 20,
        withContent: true,
        searchType: 'both',
      };

      const response = await postSupportSearch(searchParams);

      if (response.code === 0 && response.data?.items) {
        setSearchState(prev => ({
          ...prev,
          results: response.data.items || [],
          loading: false,
          total: response.data.pageInfo?.totalNumber || 0,
        }));
      } else {
        console.error('Search failed:', response.msg);
        setSearchState(prev => ({ ...prev, results: [], loading: false }));
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchState(prev => ({ ...prev, results: [], loading: false }));
    }
  }, []);

  useDebounceEffect(
    () => {
      if (searchState.keyword) {
        performSearch(searchState.keyword);
      } else {
        setSearchState(prev => ({ ...prev, results: [] }));
      }
    },
    [searchState.keyword],
    {
      wait: 300,
    },
  );

  // 生成搜索建议 - 使用两个平台的一级目录
  const searchSuggestions = useMemo(() => {
    const publisherCatalogs = catalogsByPlatform.publisher || [];
    const advertiserCatalogs = catalogsByPlatform.advertiser || [];

    const groups = [];

    // User Growth 平台建议
    if (advertiserCatalogs.length > 0) {
      groups.push({
        title: 'User Growth',
        suggestions: advertiserCatalogs.map((catalog, index) => ({
          id: `advertiser_${catalog.id || index}`,
          label: catalog.name || 'Untitled',
          url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=advertiser`,
        })),
      });
    }

    // Monetization 平台建议
    if (publisherCatalogs.length > 0) {
      groups.push({
        title: 'Monetization',
        suggestions: publisherCatalogs.map((catalog, index) => ({
          id: `publisher_${catalog.id || index}`,
          label: catalog.name || 'Untitled',
          url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=publisher`,
        })),
      });
    }

    return groups;
  }, [catalogsByPlatform]);

  // 转换搜索结果为组件需要的格式
  const searchResults = useMemo((): IGlobalSearchResultGroup[] => {
    if (searchState.results.length === 0) return [];

    // 按平台分组
    const publisherResults = searchState.results.filter(post => post.platform === 'publisher');
    const advertiserResults = searchState.results.filter(post => post.platform === 'advertiser');

    const groups: IGlobalSearchResultGroup[] = [];

    if (advertiserResults.length > 0) {
      // 如果有另一个平台的数据，展示2个，反之，展示4个
      const limit = publisherResults.length > 0 ? 2 : 4;
      groups.push({
        title: 'User Growth',
        results: advertiserResults.slice(0, limit).map((post, index) => ({
          id: `advertiser_${post.id || index}`,
          label: post.title || 'Untitled',
          url: `/supportcenter?pid=${post.id}&platform=advertiser`,
        })),
      });
    }

    if (publisherResults.length > 0) {
      // 如果有另一个平台的数据，展示2个，反之，展示4个
      const limit = advertiserResults.length > 0 ? 2 : 4;
      groups.push({
        title: 'Monetization',
        results: publisherResults.slice(0, limit).map((post, index) => ({
          id: `publisher_${post.id || index}`,
          label: post.title || 'Untitled',
          url: `/supportcenter?pid=${post.id}&platform=publisher`,
        })),
      });
    }

    return groups;
  }, [searchState.results]);

  // 搜索操作
  const searchActions: SearchActions = useMemo(() => ({
    setKeyword: (keyword: string) => {
      setSearchState(prev => ({ ...prev, keyword }));
    },
    setSelectedCategory: (category: string) => {
      setSearchState(prev => ({ ...prev, selectedCategory: category }));
    },
    performSearch,
  }), [performSearch]);

  // 处理搜索框变化
  const handleSearchChange = useCallback((_e: unknown, value: string) => {
    searchActions.setKeyword(value);
  }, [searchActions]);

  // 处理搜索框关闭
  const handleSearchClose = useCallback(() => {
    setShowGlobalSearch(false);
    searchActions.setKeyword('');
  }, [searchActions]);

  // 处理查看更多 - 跳转到搜索结果页面，保持关键词
  const handleViewMore = useCallback(() => {
    setShowGlobalSearch(false);
    const searchParams = new URLSearchParams();
    searchParams.set('current', 'support');
    searchParams.set('allResults', 'true');
    searchParams.set('keyword', searchState.keyword);
    nav.to('/', {
      search: Object.fromEntries(searchParams),
    });
  }, [nav, searchState.keyword]);

  return {
    searchState,
    searchActions,
    searchSuggestions,
    searchResults,
    showGlobalSearch,
    setShowGlobalSearch,
    handleSearchChange,
    handleSearchClose,
    handleViewMore,
  };
}

export default useSupportSearch;